import type { Aabb, InteriorTemplate, StaticCollider, Vec2 } from "@townsy/contracts";
import { YAW_NORTH, YAW_SOUTH, aabb, yawFromDirection } from "../geometry";
import { fixture } from "./common";

/**
 * Townsy Casino room (ADR-0016): ONE shared room, 40 m x 30 m. Local coordinates: the entrance and exit door are on the
 * south wall (z = -15), the room opens north. Everything the game (apps/realtime/src/venues/casino) and the renderer
 * (apps/web/src/venues/casino) both need lives here: table pivots and seats, slot machines and their play spots, the
 * money wheel and its betting counter, the bar.
 *
 *            north wall (z = 15)
 *   +-----------------------------------------------+
 *   | lounge   [sign]     WHEEL        BAR + stools |
 *   |  sofas             counter                   |
 *   |      blackjack 3                             |
 *   |                              8 slot machines |
 *   |  blackjack 1  blackjack 2   (rug)            |
 *   |                                              |
 *   |                 [chips sign]                 |
 *   +-------------------- door --------------------+
 *
 * Every fixture has a collider. The casino's own 3D draws the "venue_" ones (venue_bj_, venue_slot_, venue_wheel_,
 * venue_bar_, venue_sign_), which the generic room builder skips; plant_, sofa_ and table_ (the lounge, the potted
 * plants) are the generic builder's furniture.
 */
export const CASINO_BOUNDS: Aabb = aabb(-20, -15, 20, 15);

export interface CasinoSpot {
  x: number;
  z: number;
  /** Yaw the avatar faces there (0 = north). */
  facing: number;
}

export interface CasinoTableLayout {
  index: number;
  /** Centre of the table's straight (dealer) edge; the felt is a half disc of `radius` south of it. */
  pivot: Vec2;
  radius: number;
  /** Where the dealer stands, north of the straight edge, facing the players. */
  dealer: CasinoSpot;
  /** The five player spots around the arc, from the dealer's left (east, first to act) to the dealer's right (west). */
  seats: CasinoSpot[];
  /** Direction of each seat from the pivot (unit vectors), for cards and chips on the felt. */
  seatDirs: Vec2[];
  /** The half disc's bounding box: the distance to sit at (or walk away from) the table is measured to it. */
  footprint: Aabb;
  /** Collider boxes inscribed in the half disc (the drawn table encloses them). */
  colliders: Aabb[];
}

export interface CasinoSlotLayout {
  index: number;
  /** Cabinet centre on the floor. */
  position: Vec2;
  /** The screen faces this way (south: players stand south of the row, facing north). */
  facing: number;
  collider: Aabb;
  /** Where the player stands to play. */
  spot: CasinoSpot;
}

/** Felt radius of a blackjack table and how far from the pivot a player stands. */
export const CASINO_TABLE_RADIUS = 1.6;
const SEAT_RING = CASINO_TABLE_RADIUS + 0.75;
const DEALER_OFFSET = 0.6;
/** Seat directions in degrees east of due south: seat 1 sits on the dealer's left (east) and plays first. */
const SEAT_ANGLES = [60, 30, 0, -30, -60];

function tableLayout(index: number, px: number, pz: number): CasinoTableLayout {
  const R = CASINO_TABLE_RADIUS;
  const seatDirs = SEAT_ANGLES.map((deg) => {
    const a = (deg * Math.PI) / 180;
    return { x: Math.sin(a), z: -Math.cos(a) };
  });
  const seats = seatDirs.map((d) => {
    const x = px + d.x * SEAT_RING;
    const z = pz + d.z * SEAT_RING;
    return { x, z, facing: yawFromDirection(px - x, pz - z) };
  });
  // Three boxes inscribed in the half disc (every corner inside radius R).
  const colliders = [aabb(px - 0.55, pz - 1.5, px + 0.55, pz), aabb(px - 1.1, pz - 1.15, px + 1.1, pz), aabb(px - 1.5, pz - 0.55, px + 1.5, pz)];
  return {
    index,
    pivot: { x: px, z: pz },
    radius: R,
    dealer: { x: px, z: pz + DEALER_OFFSET, facing: YAW_SOUTH },
    seats,
    seatDirs,
    footprint: aabb(px - R, pz - R, px + R, pz),
    colliders,
  };
}

/** Three blackjack tables: two in front (south-west), one behind them. */
export const CASINO_TABLES: readonly CasinoTableLayout[] = [tableLayout(0, -15.5, -4.5), tableLayout(1, -8, -4.5), tableLayout(2, -11.75, 5.5)];

const SLOT_ROW_Z = 1.5;
const SLOT_HALF_W = 0.5;
const SLOT_HALF_D = 0.45;
/** Eight slot machines in one row (east half), screens facing south. */
export const CASINO_SLOTS: readonly CasinoSlotLayout[] = Array.from({ length: 8 }, (_, i) => {
  const x = 7 + i * 1.5;
  return {
    index: i,
    position: { x, z: SLOT_ROW_Z },
    facing: YAW_SOUTH,
    collider: aabb(x - SLOT_HALF_W, SLOT_ROW_Z - SLOT_HALF_D, x + SLOT_HALF_W, SLOT_ROW_Z + SLOT_HALF_D),
    spot: { x, z: SLOT_ROW_Z - SLOT_HALF_D - 0.75, facing: YAW_NORTH },
  };
});

/**
 * The money wheel: an upright wheel on a stage against the north wall, facing south, and a betting counter in front of
 * it. Bets are taken within `betRange` metres of `betSpot` (the middle of the counter's player side).
 */
export const CASINO_WHEEL = {
  /** Axle position on the floor plan; the wheel's face is on its south side. */
  position: { x: 0, z: 13.45 } as Vec2,
  hubY: 2.35,
  radius: 1.3,
  facing: YAW_SOUTH,
  stage: aabb(-2.2, 12.6, 2.2, 14.4),
  counter: aabb(-3.6, 9.6, 3.6, 10.3),
  betSpot: { x: 0, z: 8.9, facing: YAW_NORTH } as CasinoSpot,
  betRange: 6,
} as const;

/** The bar (north-east corner): an L-shaped counter, a back bar against the wall and eight stools. */
export const CASINO_BAR = {
  /** Standing anywhere in here counts as being at the bar. */
  area: aabb(7.5, 8.4, 20, 15),
  counter: aabb(9.5, 11.6, 20, 12.4),
  counterReturn: aabb(8.8, 11.6, 9.6, 14.3),
  backBar: aabb(9.6, 14.35, 19.6, 15),
  stools: Array.from({ length: 8 }, (_, i) => ({ x: 10.3 + i * 1.15, z: 10.95, facing: YAW_NORTH })) as CasinoSpot[],
  bartender: { x: 14.6, z: 13.35, facing: YAW_SOUTH } as CasinoSpot,
} as const;

/** The lounge (north-west): two sofas and a coffee table under the casino's sign. */
export const CASINO_LOUNGE = {
  sofaNorth: aabb(-17.2, 12.9, -13.2, 13.9),
  sofaWest: aabb(-19.4, 8.2, -18.4, 11.8),
  table: aabb(-16, 9.6, -14.4, 10.6),
} as const;

/** A freestanding sign by the entrance that explains the play chips. */
export const CASINO_CHIPS_SIGN = { position: { x: 4.4, z: -10.6 } as Vec2, facing: YAW_SOUTH, collider: aabb(4.15, -10.75, 4.65, -10.45) } as const;

const PLANTS: readonly Vec2[] = [
  { x: -3.2, z: -14.3 },
  { x: 3.2, z: -14.3 },
  { x: -19.2, z: -14.2 },
  { x: 19.2, z: -14.2 },
  { x: -4.4, z: 14.2 },
  { x: 4.4, z: 14.2 },
  { x: -19.2, z: 14.2 },
  { x: 19.2, z: 7.6 },
  { x: -19.2, z: 0.5 },
];

/** Every fixture collider of the casino room. */
export function casinoColliders(): StaticCollider[] {
  const out: StaticCollider[] = [];
  for (const t of CASINO_TABLES) t.colliders.forEach((c, k) => out.push(fixture(`venue_bj_t${t.index}_${k}`, c)));
  for (const s of CASINO_SLOTS) out.push(fixture(`venue_slot_${s.index}`, s.collider));
  out.push(fixture("venue_wheel_stage", CASINO_WHEEL.stage), fixture("venue_wheel_counter", CASINO_WHEEL.counter));
  out.push(fixture("venue_bar_counter", CASINO_BAR.counter), fixture("venue_bar_return", CASINO_BAR.counterReturn), fixture("venue_bar_back", CASINO_BAR.backBar));
  out.push(fixture("venue_sign_chips", CASINO_CHIPS_SIGN.collider));
  // The lounge and the plants are the generic room builder's furniture.
  out.push(fixture("sofa_lounge_n", CASINO_LOUNGE.sofaNorth), fixture("sofa_lounge_w", CASINO_LOUNGE.sofaWest), fixture("table_lounge", CASINO_LOUNGE.table));
  PLANTS.forEach((p, i) => out.push(fixture(`plant_${i}`, aabb(p.x - 0.4, p.z - 0.4, p.x + 0.4, p.z + 0.4))));
  return out;
}

/** Distance from a point to a box (0 inside). */
export function casinoBoxDistance(p: Vec2, b: Aabb): number {
  const dx = Math.max(b.minX - p.x, 0, p.x - b.maxX);
  const dz = Math.max(b.minZ - p.z, 0, p.z - b.maxZ);
  return Math.hypot(dx, dz);
}

export function casinoTemplate(): InteriorTemplate {
  const B = CASINO_BOUNDS;
  return {
    id: "casino_floor",
    venue: "casino",
    bounds: B,
    spawn: { position: { x: 0, z: B.minZ + 2.4 }, facing: YAW_NORTH },
    exit: { position: { x: 0, z: B.minZ + 1 }, facing: YAW_SOUTH },
    exitDoor: { position: { x: 0, z: B.minZ }, direction: { x: 0, z: -1 }, halfWidth: 1.6 },
    // The casino's name on the north wall above the lounge (the wheel stands in the middle of that wall).
    signAnchor: { kind: "storefront", position: { x: -11, y: 2.55, z: B.maxZ - 0.1 }, facing: YAW_SOUTH, width: 9, height: 1.6 },
    colliders: casinoColliders(),
  };
}
